import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { aiComplianceService } from '../../../services/aiComplianceService';
import { useLanguage } from '../../../contexts/LanguageContext';

const GRADE_STYLES = {
  A: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  B: 'bg-blue-100 text-blue-700 border-blue-200',
  C: 'bg-amber-100 text-amber-700 border-amber-200',
  D: 'bg-red-100 text-red-700 border-red-200',
};

const RISK_STYLES = {
  Low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Medium: 'bg-amber-50 text-amber-700 border-amber-200',
  High: 'bg-red-50 text-red-700 border-red-200',
};

const RISK_ICONS = {
  Low: 'ShieldCheck',
  Medium: 'ShieldAlert',
  High: 'ShieldX',
};

const SiteHealthPanel = ({ siteId, complianceAlerts = [], siteScores = [], complianceRawData = {} }) => {
  const { t } = useLanguage();
  const [aiInsights, setAiInsights] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');

  // Derive site-specific score
  const siteScore = siteScores?.find(s => s?.siteId === siteId) || siteScores?.[0];
  const score = siteScore?.score ?? null;
  const grade = siteScore?.grade || null;
  const riskLevel = siteScore?.riskLevel || 'Low';

  // Filter alerts to this site
  const siteAlerts = complianceAlerts?.filter(a => a?.siteId === siteId || !siteId);

  // Open snags count
  const openSnags = (complianceRawData?.snags || [])
    ?.filter(s => s?.site_id === siteId && s?.status !== 'resolved')?.length ?? 0;

  // Incidents this week
  const oneWeekAgo = new Date();
  oneWeekAgo?.setDate(oneWeekAgo?.getDate() - 7);
  const incidentsThisWeek = (complianceRawData?.incidents || [])
    ?.filter(i => i?.site_id === siteId && new Date(i?.created_at) >= oneWeekAgo)?.length ?? 0;

  // Overdue inspections — sites with no completed audit in last 30 days
  const overdueInspections = siteAlerts?.filter(a => a?.type === 'overdue_inspection')?.length ?? 0;

  // Documents expiring within 30 days
  const expiringDocs = (complianceRawData?.documents || [])
    ?.filter(d => {
      if (d?.site_id !== siteId) return false;
      if (!d?.expiry_date) return false;
      const daysUntil = d?.days_until_expiry ?? Math.ceil((new Date(d?.expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
      return daysUntil >= 0 && daysUntil <= 30;
    })?.length ?? 0;

  const loadAiInsights = async () => {
    if (aiLoading) return;
    setAiLoading(true);
    setAiError('');
    try {
      const siteName = siteScore?.siteName || 'This site';
      const siteSnags = (complianceRawData?.snags || [])?.filter(s => s?.site_id === siteId);
      const siteIncidents = (complianceRawData?.incidents || [])?.filter(i => i?.site_id === siteId);
      const siteTasks = (complianceRawData?.tasks || [])?.filter(t => t?.site_id === siteId);
      const siteActivities = (complianceRawData?.activities || [])?.filter(a => a?.site_id === siteId);
      const siteDocs = (complianceRawData?.documents || [])?.filter(d => d?.site_id === siteId);

      const result = await aiComplianceService?.analyseSite({
        siteName,
        score: score ?? 0,
        riskLevel,
        alerts: siteAlerts,
        incidents: siteIncidents,
        snags: siteSnags,
        tasks: siteTasks,
        activities: siteActivities,
        documents: siteDocs,
      });

      setAiInsights(result?.insights?.slice(0, 3) || []);
    } catch (err) {
      console.error('[SiteHealthPanel] AI insights error:', err);
      // Surface the real error message — analyseSite already returns a soft
      // fallback `{ insights: ['Unable to generate...'] }` on its own catch,
      // so this branch only fires for unexpected throws upstream. Keep the
      // copy short; the button is the retry mechanism.
      setAiError(err?.message ? `Couldn't generate insights: ${err.message}` : "Couldn't generate insights. Try again.");
    } finally {
      setAiLoading(false);
    }
  };

  // Reset insights when the active site changes so a stale previous-site
  // analysis doesn't sit visible above a button that now means something
  // different. The user explicitly clicks Generate to fetch for the new site.
  useEffect(() => {
    setAiInsights([]);
    setAiError('');
  }, [siteId]);

  const metrics = [
    {
      labelKey: 'siteHealthOpenSnags',
      value: openSnags,
      icon: 'Wrench',
      iconBg: openSnags > 0 ? 'bg-orange-50' : 'bg-emerald-50',
      iconColor: openSnags > 0 ? 'text-orange-600' : 'text-emerald-600',
      valueColor: openSnags > 0 ? 'text-orange-700' : 'text-foreground',
    },
    {
      labelKey: 'siteHealthIncidents7d',
      value: incidentsThisWeek,
      icon: 'AlertOctagon',
      iconBg: incidentsThisWeek > 0 ? 'bg-red-50' : 'bg-emerald-50',
      iconColor: incidentsThisWeek > 0 ? 'text-red-600' : 'text-emerald-600',
      valueColor: incidentsThisWeek > 0 ? 'text-red-700' : 'text-foreground',
    },
    {
      labelKey: 'siteHealthOverdueInspections',
      value: overdueInspections,
      icon: 'ClipboardX',
      iconBg: overdueInspections > 0 ? 'bg-amber-50' : 'bg-emerald-50',
      iconColor: overdueInspections > 0 ? 'text-amber-600' : 'text-emerald-600',
      valueColor: overdueInspections > 0 ? 'text-amber-700' : 'text-foreground',
    },
    {
      labelKey: 'siteHealthExpiringDocs',
      value: expiringDocs,
      icon: 'FileWarning',
      iconBg: expiringDocs > 0 ? 'bg-amber-50' : 'bg-emerald-50',
      iconColor: expiringDocs > 0 ? 'text-amber-600' : 'text-emerald-600',
      valueColor: expiringDocs > 0 ? 'text-amber-700' : 'text-foreground',
    },
  ];

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-1 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
            <Icon name="Activity" size={16} className="text-accent" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground font-heading">{t('siteHealthTitle')}</h3>
            <p className="text-xs text-muted-foreground">{t('siteHealthSubtitle')}</p>
          </div>
        </div>
        {riskLevel && (
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${RISK_STYLES?.[riskLevel] || RISK_STYLES?.Low}`}>
            <Icon name={RISK_ICONS?.[riskLevel] || 'Shield'} size={12} />
            {riskLevel} {t('siteHealthRisk')}
          </span>
        )}
      </div>
      <div className="p-5 space-y-4">
        {/* Compliance Score */}
        {score !== null ? (
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/40 border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                <Icon name="ShieldCheck" size={20} className="text-accent" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">{t('siteHealthComplianceScore')}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-2xl font-bold text-foreground">{score}</span>
                  <span className="text-sm text-muted-foreground">/100</span>
                </div>
              </div>
            </div>
            {grade && (
              <span className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center text-lg font-bold ${GRADE_STYLES?.[grade] || GRADE_STYLES?.D}`}>
                {grade}
              </span>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 border border-border">
            <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
              <Icon name="ShieldCheck" size={20} className="text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">{t('siteHealthScoreLoading')}</p>
          </div>
        )}

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 gap-2.5">
          {metrics?.map(metric => (
            <div key={metric?.labelKey} className="flex items-center gap-2.5 p-3 rounded-lg border border-border bg-background">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${metric?.iconBg}`}>
                <Icon name={metric?.icon} size={15} className={metric?.iconColor} />
              </div>
              <div className="min-w-0">
                <p className={`text-base font-bold leading-none ${metric?.valueColor}`}>{metric?.value}</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">{t(metric?.labelKey)}</p>
              </div>
            </div>
          ))}
        </div>

        {/* AI Compliance Insights — explicit-trigger only. We removed the
            auto-fire useEffect so dashboard mounts no longer burn a paid AI
            call (and no longer surface a 429 in DevTools when the daily cap
            or OpenAI's RPM is hit). The button is also the retry path. */}
        <div className="rounded-lg border border-border bg-background overflow-hidden">
          <div className="px-3 py-2.5 border-b border-border flex items-center gap-1.5">
            <Icon name="Sparkles" size={13} className="text-accent" />
            <span className="text-xs font-semibold text-foreground">{t('siteHealthAiInsights')}</span>
          </div>

          <div className="p-3 space-y-2.5">
            {aiInsights?.length > 0 ? (
              <ul className="space-y-2">
                {aiInsights?.map((insight, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent mt-1.5 flex-shrink-0" />
                    <p className="text-xs text-foreground leading-relaxed">{insight}</p>
                  </li>
                ))}
              </ul>
            ) : (
              !aiLoading && (
                <p className="text-xs text-muted-foreground leading-relaxed">
                  AI insights provide a short analysis of this site's compliance trends. Click below to generate.
                </p>
              )
            )}

            {aiError && (
              <div className="flex items-start gap-2 rounded-md bg-destructive/10 border border-destructive/20 px-2.5 py-2">
                <Icon name="AlertCircle" size={12} className="text-destructive mt-0.5 flex-shrink-0" />
                <p className="text-xs text-destructive break-words">{aiError}</p>
              </div>
            )}

            <button
              type="button"
              onClick={loadAiInsights}
              disabled={aiLoading || !siteId}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                aiInsights?.length > 0
                  ? 'border border-border bg-background text-foreground hover:bg-muted'
                  : 'bg-accent text-accent-foreground hover:bg-accent/90'
              }`}
            >
              {aiLoading ? (
                <>
                  <div className="w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin" />
                  Generating…
                </>
              ) : (
                <>
                  <Icon name={aiInsights?.length > 0 ? 'RefreshCw' : 'Sparkles'} size={12} />
                  {aiInsights?.length > 0 ? 'Refresh insights' : 'Generate insights'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SiteHealthPanel;
